import * as THREE from 'three';
import type { SketchFace, SketchSolid, SketchVec3 } from '../../core/sketch/types';
import { parseFaceSelectionId, parseEdgeSelectionId, parseVertexSelectionId, parseLooseEdgeSelectionId, solidEffectiveVisible } from '../../core/sketch/types';
import { faceArea } from '../../core/sketch/measure';
import { splitFaceWithSegments } from '../../core/sketch/faceSplit';
import { faceIntersectionSegment } from '../../core/sketch/faceIntersect';
import { reverseFace, orientSolidFaces, flipSolidAlong } from '../../core/sketch/orient';
import { useSketchStore } from '../../state/useSketchStore';
import { useAppStore } from '../../state/store';
import { notify } from '../../utils/notify';
import { PANEL_EVENTS } from '@suite/panels';
import { armSketchAlignView } from './SketchCameraTools';

/**
 * Sketch context-menu command actions (the SketchUp-style right-click menu).
 *
 * One table drives two consumers so the ids can never drift apart:
 * - CommandRegistrar registers every entry (category "Sketch", scope
 *   "sketch") so the ContextMenu shell resolves the menu's `action` ids and
 *   the entries stay visible in the Keyboard Shortcuts editor.
 * - config/sketchContextMenu.ts builds the menu tree from the SAME ids,
 *   choosing which entries apply to the current selection.
 *
 * Every action reads the live sketch state imperatively (getState) — the
 * menu is built at right-click time and the command can also fire later
 * from a shortcut, so nothing may depend on render-time props.
 */

interface FaceRef { solidId: string; faceId: string }

/** Face composites in the selection that still exist. */
function selectedFaceRefs(): FaceRef[] {
    const state = useSketchStore.getState();
    const refs: FaceRef[] = [];
    for (const id of state.selectedIds) {
        const parsed = parseFaceSelectionId(id);
        if (!parsed) continue;
        const solid = state.solids[parsed.solidId];
        if (solid?.faces.some((f) => f.id === parsed.faceId)) refs.push(parsed);
    }
    return refs;
}

/** Plain solid ids in the selection (whole-solid entries only). */
function selectedSolidIds(): string[] {
    const state = useSketchStore.getState();
    return state.selectedIds.filter((id) => Boolean(state.solids[id]));
}

/** Every solid the selection touches: composite owners + whole solids. */
function ownerSolidIds(): string[] {
    const state = useSketchStore.getState();
    const ids = new Set<string>();
    for (const id of state.selectedIds) {
        const parsedFace = parseFaceSelectionId(id);
        if (parsedFace && state.solids[parsedFace.solidId]) ids.add(parsedFace.solidId);
        else {
            const parsedEdge = parseEdgeSelectionId(id);
            if (parsedEdge && state.solids[parsedEdge.solidId]) ids.add(parsedEdge.solidId);
            else {
                const parsedVertex = parseVertexSelectionId(id);
                if (parsedVertex && state.solids[parsedVertex.solidId]) ids.add(parsedVertex.solidId);
                else if (state.solids[id]) ids.add(id);
            }
        }
    }
    return Array.from(ids);
}

/** Visible + not tag-hidden solids (ops never touch invisible geometry). */
function visibleSolids(): Array<{ id: string; solid: SketchSolid }> {
    const state = useSketchStore.getState();
    return Object.values(state.solids)
        .filter((solid) => solidEffectiveVisible(solid, state.tags))
        .map((solid) => ({ id: solid.id, solid }));
}

/** Effective material of a face (falls back to its solid's material). */
function effectiveMaterial(face: SketchFace, solidMaterialId: string): string {
    return face.materialId ?? solidMaterialId;
}

function syncAppSelection(sketchIds: string[]): void {
    // The app-level selection mirrors solid ids (the Gumball and EntityLayer
    // read it); composite ids reduce to their owner solid. Sketch entities
    // the app store cannot mirror (loose edges, guides) drop out of the
    // mirror — the gizmo has no target to sit on for them.
    const sketch = useSketchStore.getState();
    const ownerIds = Array.from(new Set(
        sketchIds.flatMap((id): string[] => {
            const solidId = parseFaceSelectionId(id)?.solidId
                ?? parseEdgeSelectionId(id)?.solidId
                ?? parseVertexSelectionId(id)?.solidId;
            if (solidId) return [solidId];
            if (parseLooseEdgeSelectionId(id)) return [];
            return sketch.solids[id] ? [id] : [];
        })
    ));
    useAppStore.getState().select(ownerIds);
}

// ============================== ENTITY INFO ==============================

/** Focus the Entity Info tray section (Sketch's Entity Info). */
function showEntityInfo(): void {
    window.dispatchEvent(new CustomEvent(PANEL_EVENTS.activateTab, {
        detail: { panelId: 'entity-info' },
    }));
}

// ============================== ERASE / HIDE ==============================

function eraseSelection(): void {
    const sketch = useSketchStore.getState();
    if (sketch.selectedIds.length === 0) return;
    const guideIds = sketch.selectedIds
        .filter((id) => id.startsWith('guide:'))
        .map((id) => id.slice('guide:'.length));
    if (guideIds.length > 0) sketch.removeGuides(guideIds, 'Erase');
    const erasedSolidIds = new Set(ownerSolidIds());
    sketch.deleteSelected('Erase');
    // The app mirror must not keep pointing at erased geometry.
    const app = useAppStore.getState();
    const remaining = app.selectedIds.filter((id) => !erasedSolidIds.has(id));
    app.select(remaining);
}

/** Solids hidden by the last Hide (Unhide Last; Sketch parity). */
let lastHiddenSolidIds: string[] = [];

function hideSelection(): void {
    const sketch = useSketchStore.getState();
    const ownerIds = ownerSolidIds();
    if (ownerIds.length === 0) return;
    lastHiddenSolidIds = ownerIds;
    sketch.setSolidsVisible(ownerIds, false, 'Hide');
    sketch.select([]);
    useAppStore.getState().select([]);
}

function unhideAll(): void {
    const sketch = useSketchStore.getState();
    const hidden = Object.values(sketch.solids)
        .filter((solid) => !solid.visible)
        .map((solid) => solid.id);
    sketch.setSolidsVisible(hidden, true, 'Unhide All');
}

function unhideLast(): void {
    const sketch = useSketchStore.getState();
    const ids = lastHiddenSolidIds.filter((id) => sketch.solids[id] && !sketch.solids[id].visible);
    sketch.setSolidsVisible(ids, true, 'Unhide Last');
}

// ============================== SELECT ==============================

function selectAll(): void {
    const ids = visibleSolids().map(({ id }) => id);
    useSketchStore.getState().select(ids);
    syncAppSelection(ids);
}

function selectNone(): void {
    useSketchStore.getState().select([]);
    useAppStore.getState().select([]);
}

function selectInverse(): void {
    const selected = new Set(ownerSolidIds());
    const ids = visibleSolids()
        .map(({ id }) => id)
        .filter((id) => !selected.has(id));
    useSketchStore.getState().select(ids);
    syncAppSelection(ids);
}

/** Every face sharing the picked entity's effective material. */
function selectSameMaterial(): void {
    const sketch = useSketchStore.getState();
    const faceRefs = selectedFaceRefs();
    const solidIds = selectedSolidIds();
    const next: string[] = [];
    if (faceRefs.length > 0) {
        const { solidId, faceId } = faceRefs[0];
        const solid = sketch.solids[solidId];
        const face = solid?.faces.find((f) => f.id === faceId);
        if (!solid || !face) return;
        const material = effectiveMaterial(face, solid.materialId);
        for (const candidate of visibleSolids()) {
            for (const f of candidate.solid.faces) {
                if (effectiveMaterial(f, candidate.solid.materialId) === material) {
                    next.push(`${candidate.id}:${f.id}`);
                }
            }
        }
    } else if (solidIds.length > 0) {
        const material = sketch.solids[solidIds[0]]?.materialId;
        if (!material) return;
        for (const candidate of visibleSolids()) {
            if (candidate.solid.materialId === material) next.push(candidate.id);
        }
    } else {
        return;
    }
    sketch.select(next);
    syncAppSelection(next);
}

// ============================== AREA ==============================

function areaOfSolid(solid: SketchSolid): number {
    return solid.faces.reduce((sum, face) => sum + faceArea(face), 0);
}

function reportAreaSelection(): void {
    const sketch = useSketchStore.getState();
    let total = 0;
    for (const ref of selectedFaceRefs()) {
        const solid = sketch.solids[ref.solidId];
        const face = solid?.faces.find((f) => f.id === ref.faceId);
        if (face) total += faceArea(face);
    }
    for (const id of selectedSolidIds()) {
        const solid = sketch.solids[id];
        if (solid) total += areaOfSolid(solid);
    }
    notify(`Selection area: ${total.toFixed(3)} m²`);
}

function reportAreaFaces(): void {
    const total = visibleSolids().reduce((sum, { solid }) => sum + areaOfSolid(solid), 0);
    notify(`Total face area: ${total.toFixed(3)} m²`);
}

// ============================== INTERSECT FACES ==============================

/**
 * Split the selected faces where other solids' faces cross them. `context`
 * restricts the candidates to solids in the same group (or the same
 * top-level ungrouped context) — Sketch's With Context.
 */
function intersectFaces(context: boolean): void {
    const sketch = useSketchStore.getState();
    const targets = selectedFaceRefs();
    if (targets.length === 0) {
        notify('Select faces to intersect.', 'warning');
        return;
    }

    const targetBySolid = new Map<string, Set<string>>();
    for (const ref of targets) {
        const set = targetBySolid.get(ref.solidId) ?? new Set<string>();
        set.add(ref.faceId);
        targetBySolid.set(ref.solidId, set);
    }

    const groupOf = (solidId: string): string | null =>
        sketch.solids[solidId]?.groupId ?? null;
    const contextMatches = (targetSolidId: string, candidateSolidId: string): boolean => {
        if (!context) return true;
        const tg = groupOf(targetSolidId);
        const cg = groupOf(candidateSolidId);
        return tg === cg;
    };

    let splitCount = 0;
    for (const [targetSolidId, faceIds] of targetBySolid) {
        const solid = sketch.solids[targetSolidId];
        if (!solid) continue;
        // Candidate faces: every visible solid except the target itself,
        // restricted to the shared context for With Context.
        const candidates: Array<{ solidId: string; face: SketchFace }> = [];
        for (const { id, solid: candidateSolid } of visibleSolids()) {
            if (id === targetSolidId) continue;
            if (!contextMatches(targetSolidId, id)) continue;
            for (const face of candidateSolid.faces) candidates.push({ solidId: id, face });
        }

        const segmentsByFace = new Map<string, SketchVec3[][]>();
        for (const faceId of faceIds) {
            const face = solid.faces.find((f) => f.id === faceId);
            if (!face) continue;
            for (const { face: candidateFace } of candidates) {
                const segment = faceIntersectionSegment(face, candidateFace);
                if (!segment) continue;
                const list = segmentsByFace.get(faceId) ?? [];
                list.push(segment);
                segmentsByFace.set(faceId, list);
            }
        }
        if (segmentsByFace.size === 0) continue;

        const faces = solid.faces.flatMap((face) => {
            const segments = segmentsByFace.get(face.id);
            if (!segments || segments.length === 0) return [face];
            const result = splitFaceWithSegments(face, segments);
            if (result.ok) {
                splitCount += result.faces.length > 1 ? 1 : 0;
                return result.faces;
            }
            return [face];
        });
        sketch.updateSolid(solid.id, { ...solid, faces }, 'Intersect Faces');
    }

    if (splitCount > 0) {
        // Split faces get fresh ids — drop dead composites from the selection.
        const sketch2 = useSketchStore.getState();
        const alive = sketch2.selectedIds.filter((id) => {
            const parsed = parseFaceSelectionId(id);
            if (!parsed) return true;
            return sketch2.solids[parsed.solidId]?.faces.some((f) => f.id === parsed.faceId);
        });
        sketch2.select(alive);
        syncAppSelection(alive);
        notify(`Intersected faces: ${splitCount} face(s) split.`);
    } else {
        notify('No face intersections found.', 'info');
    }
}

// ============================== ALIGN VIEW / AXES ==============================

function alignView(): void {
    const ref = selectedFaceRefs()[0];
    if (!ref) return;
    armSketchAlignView(ref.solidId, ref.faceId);
    useAppStore.getState().setActiveTool('alignView');
}

/** Place the drawing axes on the picked face (Sketch's Align Axes). */
function alignAxes(): void {
    const sketch = useSketchStore.getState();
    const ref = selectedFaceRefs()[0];
    const solid = ref ? sketch.solids[ref.solidId] : null;
    const face = solid?.faces.find((f) => f.id === ref!.faceId);
    if (!solid || !face) return;
    const normal = new THREE.Vector3(...face.normal).normalize();
    // X along the longest outer edge, kept in-plane.
    let xAxis: THREE.Vector3 | null = null;
    let bestLen = 0;
    for (let i = 0; i < face.outer.length; i++) {
        const a = new THREE.Vector3(...face.outer[i]);
        const b = new THREE.Vector3(...face.outer[(i + 1) % face.outer.length]);
        const len = a.distanceTo(b);
        if (len > bestLen) {
            bestLen = len;
            xAxis = b.clone().sub(a).normalize();
        }
    }
    if (!xAxis || bestLen < 1e-9) return;
    const yAxis = new THREE.Vector3().crossVectors(normal, xAxis).normalize();
    sketch.setDrawingAxes({
        origin: [...face.origin] as SketchVec3,
        xAxis: [xAxis.x, xAxis.y, xAxis.z],
        yAxis: [yAxis.x, yAxis.y, yAxis.z],
    });
}

// ============================== REVERSE / ORIENT / FLIP ==============================

function reverseFaces(): void {
    const sketch = useSketchStore.getState();
    const faceRefs = selectedFaceRefs();
    const updates: Array<{ id: string; next: SketchSolid }> = [];
    if (faceRefs.length > 0) {
        const bySolid = new Map<string, Set<string>>();
        faceRefs.forEach(({ solidId, faceId }) => {
            const set = bySolid.get(solidId) ?? new Set<string>();
            set.add(faceId);
            bySolid.set(solidId, set);
        });
        bySolid.forEach((faceIds, solidId) => {
            const solid = sketch.solids[solidId];
            if (!solid) return;
            updates.push({
                id: solidId,
                next: {
                    ...solid,
                    faces: solid.faces.map((f) => (faceIds.has(f.id) ? reverseFace(f) : f)),
                },
            });
        });
    } else {
        for (const solidId of selectedSolidIds()) {
            const solid = sketch.solids[solidId];
            if (!solid) continue;
            updates.push({
                id: solidId,
                next: { ...solid, faces: solid.faces.map((f) => reverseFace(f)) },
            });
        }
    }
    if (updates.length === 0) return;
    sketch.updateSolids(updates, 'Reverse Faces');
}

function orientFaces(): void {
    const sketch = useSketchStore.getState();
    const updates: Array<{ id: string; next: SketchSolid }> = [];
    let flipped = 0;
    for (const solidId of ownerSolidIds()) {
        const solid = sketch.solids[solidId];
        if (!solid) continue;
        const result = orientSolidFaces(solid.faces);
        flipped += result.flipped;
        if (result.flipped > 0) {
            updates.push({ id: solidId, next: { ...solid, faces: result.faces } });
        }
    }
    if (updates.length > 0) sketch.updateSolids(updates, 'Orient Faces');
    notify(flipped > 0 ? `Oriented faces: ${flipped} face(s) flipped.` : 'Faces are already consistent.');
}

function flipAlong(axis: 'x' | 'y' | 'z'): void {
    const sketch = useSketchStore.getState();
    const updates = selectedSolidIds()
        .map((solidId) => sketch.solids[solidId])
        .filter(Boolean)
        .map((solid) => ({ id: solid.id, next: flipSolidAlong(solid, axis) }));
    if (updates.length === 0) {
        notify('Select whole solids to flip.', 'warning');
        return;
    }
    sketch.updateSolids(updates, `Flip Along ${axis.toUpperCase()}`);
}

// ============================== LOCK / GROUP / COMPONENT ==============================

function setLocked(locked: boolean): void {
    const sketch = useSketchStore.getState();
    const updates = ownerSolidIds()
        .map((solidId) => sketch.solids[solidId])
        .filter((solid) => solid && solid.locked !== locked)
        .map((solid) => ({
            id: solid.id,
            next: { ...solid, locked },
        }));
    if (updates.length === 0) return;
    sketch.updateSolids(updates, locked ? 'Lock' : 'Unlock');
}

function makeGroup(): void {
    const sketch = useSketchStore.getState();
    // Any sketch selection qualifies: face composites resolve to their owner
    // solids (groups are solid-level containers in this document model).
    const ids = ownerSolidIds();
    if (ids.length === 0) {
        notify('Select geometry to group.', 'warning');
        return;
    }
    const groupId = sketch.groupSolids(ids);
    if (groupId) {
        const name = useSketchStore.getState().groups[groupId]?.name ?? 'Group';
        notify(`Group created: ${name}.`);
    }
}

function makeComponent(): void {
    const sketch = useSketchStore.getState();
    // Same owner-solid resolution as Make Group: a face pick stands for the
    // solid that owns it.
    const ids = ownerSolidIds();
    if (ids.length === 0) {
        notify('Select geometry to make a component.', 'warning');
        return;
    }
    const defId = sketch.saveAsComponent(ids);
    if (defId) {
        const name = useSketchStore.getState().componentDefs[defId]?.name ?? 'Component';
        notify(`Component created: ${name}. Insert it from the Components panel.`);
    }
}

function ungroup(): void {
    const sketch = useSketchStore.getState();
    const ids = ownerSolidIds().filter((solidId) => sketch.solids[solidId]?.groupId);
    if (ids.length === 0) return;
    sketch.ungroupSolids(ids);
}

// ============================== CLIPPING BOX ==============================

function createClippingBox(): void {
    const sketch = useSketchStore.getState();
    const box = new THREE.Box3();
    let found = false;
    const expand = (loop: readonly SketchVec3[]): void => {
        loop.forEach((p) => {
            box.expandByPoint(new THREE.Vector3(...p));
            found = true;
        });
    };
    const walkSolid = (solid: SketchSolid): void => {
        solid.faces.forEach((face) => {
            expand(face.outer);
            face.holes.forEach(expand);
        });
    };
    // Selection bounds when something is selected, model bounds otherwise.
    if (sketch.selectedIds.length > 0) {
        for (const entry of selectionLoopSource()) {
            entry.faces.forEach((face) => {
                expand(face.outer);
                face.holes.forEach(expand);
            });
        }
    } else {
        Object.values(sketch.solids)
            .filter((solid) => solid.visible)
            .forEach(walkSolid);
    }
    if (!found) {
        notify('Nothing to clip — the model is empty.', 'warning');
        return;
    }
    const min: SketchVec3 = [box.min.x, box.min.y, box.min.z];
    const max: SketchVec3 = [box.max.x, box.max.y, box.max.z];
    // 2% margin so the shell faces at the bounds stay visible.
    const margin = Math.max(
        max[0] - min[0],
        max[1] - min[1],
        max[2] - min[2],
        0.001,
    ) * 0.02;
    sketch.setSectionBox({
        min: [min[0] - margin, min[1] - margin, min[2] - margin],
        max: [max[0] + margin, max[1] + margin, max[2] + margin],
    });
    notify('Clipping box created. Remove it from the empty-space right-click menu or the Section Plane tool.');
}

/** Face loops of the current selection, flattened (clipping-box bounds). */
function selectionLoopSource(): Array<{ faces: SketchFace[] }> {
    const sketch = useSketchStore.getState();
    const out: Array<{ faces: SketchFace[] }> = [];
    for (const id of sketch.selectedIds) {
        const parsed = parseFaceSelectionId(id);
        if (parsed) {
            const solid = sketch.solids[parsed.solidId];
            const face = solid?.faces.find((f) => f.id === parsed.faceId);
            if (face) out.push({ faces: [face] });
            continue;
        }
        const solid = sketch.solids[id];
        if (solid) out.push({ faces: solid.faces });
    }
    return out;
}

function removeClippingBox(): void {
    useSketchStore.getState().setSectionBox(null);
}

// ============================== REGISTRATION TABLE ==============================

export interface SketchContextCommand {
    id: string;
    label: string;
    description: string;
    action: () => void;
}

const flipAxisOf = (axis: 'x' | 'y' | 'z', label: string): SketchContextCommand => ({
    id: `sketchCtxFlip${axis.toUpperCase()}`,
    label: `Flip Along ${label}`,
    description: `Mirrors the selected solids along the ${label} axis through their center.`,
    action: () => flipAlong(axis),
});

/**
 * The Sketch context-menu command surface. Ids mirror the menu builder in
 * config/sketchContextMenu.ts; labels match the SketchUp menu names.
 */
export const SKETCH_CONTEXT_COMMANDS: SketchContextCommand[] = [
    { id: 'sketchCtxEntityInfo', label: 'Entity Info', description: 'Focuses the Entity Info panel for the selection.', action: showEntityInfo },
    { id: 'sketchCtxErase', label: 'Erase', description: 'Erases the selected sketch entities.', action: eraseSelection },
    { id: 'sketchCtxHide', label: 'Hide', description: 'Hides the solids of the selection.', action: hideSelection },
    { id: 'sketchCtxUnhideAll', label: 'Unhide All', description: 'Restores every hidden solid.', action: unhideAll },
    { id: 'sketchCtxUnhideLast', label: 'Unhide Last', description: 'Restores the solids hidden by the last Hide.', action: unhideLast },
    { id: 'sketchCtxSelectAll', label: 'Select All', description: 'Selects every visible solid.', action: selectAll },
    { id: 'sketchCtxSelectNone', label: 'Select None', description: 'Clears the sketch selection.', action: selectNone },
    { id: 'sketchCtxSelectInverse', label: 'Invert Selection', description: 'Selects every visible solid that is not selected.', action: selectInverse },
    { id: 'sketchCtxSelectSameMaterial', label: 'All with Same Material', description: 'Selects every face sharing the picked entity’s material.', action: selectSameMaterial },
    { id: 'sketchCtxAreaSelection', label: 'Area of Selection', description: 'Reports the combined area of the selected faces and solids.', action: reportAreaSelection },
    { id: 'sketchCtxAreaFaces', label: 'Area of Faces', description: 'Reports the total face area of the model.', action: reportAreaFaces },
    { id: 'sketchCtxIntersectModel', label: 'Intersect Faces with Model', description: 'Splits the selected faces where other model faces cross them.', action: () => intersectFaces(false) },
    { id: 'sketchCtxIntersectContext', label: 'Intersect Faces with Context', description: 'Splits the selected faces against faces of the same group context only.', action: () => intersectFaces(true) },
    { id: 'sketchCtxAlignView', label: 'Align View', description: 'Moves the camera perpendicular to the picked face.', action: alignView },
    { id: 'sketchCtxAlignAxes', label: 'Align Axes', description: 'Places the drawing axes on the picked face.', action: alignAxes },
    { id: 'sketchCtxReverseFaces', label: 'Reverse Faces', description: 'Flips the front side of the selected faces.', action: reverseFaces },
    { id: 'sketchCtxOrientFaces', label: 'Orient Faces', description: 'Makes the faces of the selected solids consistently oriented.', action: orientFaces },
    flipAxisOf('x', 'Red (X)'),
    flipAxisOf('y', 'Green (Y)'),
    flipAxisOf('z', 'Blue (Z)'),
    { id: 'sketchCtxLock', label: 'Lock', description: 'Locks the selected solids against edits.', action: () => setLocked(true) },
    { id: 'sketchCtxUnlock', label: 'Unlock', description: 'Unlocks the selected solids.', action: () => setLocked(false) },
    { id: 'sketchCtxMakeGroup', label: 'Make Group', description: 'Groups the selected solids.', action: makeGroup },
    { id: 'sketchCtxMakeComponent', label: 'Make Component', description: 'Saves the selected solids as a reusable component definition.', action: makeComponent },
    { id: 'sketchCtxUngroup', label: 'Ungroup', description: 'Removes the group membership of the selection.', action: ungroup },
    { id: 'sketchCtxZoomSelection', label: 'Zoom Selection', description: 'Frames the selection in the view.', action: () => useAppStore.getState().setActiveTool('zoomSelection') },
    { id: 'sketchCtxCreateClippingBox', label: 'Create Clipping Box from Selection', description: 'Clips the view to the selection bounds.', action: createClippingBox },
    { id: 'sketchCtxRemoveClippingBox', label: 'Remove Clipping Box', description: 'Turns the clipping box off.', action: removeClippingBox },
];
